/**
 * Chat-bubble components — leaves 9 + 10 of the Preact rewrite.
 *
 * Each component renders the full `.sp-msg.sp-msg-X > .sp-msg-content`
 * shape so it can be mounted directly into a `display:contents` anchor
 * and become a flex child of `.sp-body-inner`.
 *
 * Leaf 9 (static): AssistantBubble, ErrorBubble, TypingBubble — write
 * once and never mutated after mount.
 *
 * Leaf 10 (live):
 * - StreamingAssistantBubble hosts the imperative createStreamingText
 *   element via a ref callback. The controller owns its own DOM
 *   children (token spans → markdown swap) so we just slot its root
 *   into the bubble's content div on mount.
 * - UserBubble takes a text Signal so voice-mode whisper deltas can
 *   live-mutate the bubble without re-mounting it. finalizeUserMessage
 *   swaps the signal value with the canonical final transcript.
 */

import type { Signal } from '@preact/signals';
import { renderMarkdown } from './markdown';

export function AssistantBubble({ markdown }: { markdown: string }) {
  return (
    <div class="sp-msg sp-msg-assistant">
      <div
        class="sp-msg-content markdown-content"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }}
      />
    </div>
  );
}

export function ErrorBubble({ message }: { message: string }) {
  return (
    <div class="sp-msg sp-msg-assistant sp-msg-error">
      <div class="sp-msg-content">
        <div class="sp-msg-text">{message}</div>
      </div>
    </div>
  );
}

export function TypingBubble() {
  return (
    <div class="sp-msg sp-msg-assistant sp-msg-typing">
      <div class="sp-msg-content">
        <div class="sp-typing">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

/**
 * Thinking-status bubble. Lives in the same slot as TypingBubble — also
 * carries `sp-msg-typing` so the existing removeTypingIndicator() path
 * clears it on the first message chunk. `label` is a Signal so a
 * sequence of tool calls swaps the text in place without remounting.
 */
export function ThinkingBubble({ label }: { label: Signal<string> }) {
  return (
    <div class="sp-msg sp-msg-assistant sp-msg-typing sp-msg-thinking">
      <div class="sp-msg-content">
        <span class="sp-thinking-label">{label.value}</span>
        <div class="sp-typing sp-typing--inline">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}

export function StreamingAssistantBubble({
  streamingEl,
}: {
  streamingEl: HTMLElement;
}) {
  // Ref callback fires synchronously during Preact's commit phase, so
  // the streaming element is attached the instant the bubble mounts.
  // The check guards against double-append on re-renders.
  const attach = (host: HTMLDivElement | null): void => {
    if (host && streamingEl.parentNode !== host) {
      host.appendChild(streamingEl);
    }
  };
  return (
    <div class="sp-msg sp-msg-assistant">
      <div class="sp-msg-content" ref={attach} />
    </div>
  );
}

export function UserBubble({ text }: { text: Signal<string> }) {
  return (
    <div class="sp-msg sp-msg-user">
      <div class="sp-msg-content">
        <div class="sp-msg-text">{text.value}</div>
      </div>
    </div>
  );
}

/**
 * Rep-authored message. Visually distinct from the AI agent — Text
 * Relay v1: a human teammate has taken over the chat. Rendered with
 * the rep's name + avatar (when supplied) above the bubble so the
 * visitor knows they're talking to a person, not the bot. Markdown is
 * NOT rendered (treat rep text as plain prose to avoid surprises from
 * pasted content with characters that look like markdown).
 */
export function RepBubble({
  text,
  repName,
  repAvatarUrl,
}: {
  text: string;
  repName?: string;
  repAvatarUrl?: string;
}) {
  const name = repName?.trim() || 'Team';
  const initial = name[0]?.toUpperCase() ?? '·';
  return (
    <div class="sp-msg sp-msg-assistant sp-msg-rep">
      <div class="sp-msg-rep-meta">
        {repAvatarUrl ? (
          <img
            class="sp-msg-rep-avatar"
            src={repAvatarUrl}
            alt={name}
          />
        ) : (
          <span class="sp-msg-rep-avatar sp-msg-rep-avatar--fallback">
            {initial}
          </span>
        )}
        <span class="sp-msg-rep-name">{name}</span>
      </div>
      <div class="sp-msg-content">
        <div class="sp-msg-text">{text}</div>
      </div>
    </div>
  );
}

/**
 * Slice F — call invite card. Visitor accepts → Daily Prebuilt overlay
 * mounts. Decline → POST /call/:id/decline, admin sees notice.
 * Rendered inline in the chat thread (not over the host page) so
 * declining is one click and accepting is friction-free.
 *
 * Imperative `onAccept` / `onDecline` are wired by the ui.tsx handler
 * since they trigger non-Preact side-effects (overlay mount + POST).
 */
export function CallInviteCard({
  repName,
  repAvatarUrl,
  onAccept,
  onDecline,
}: {
  repName?: string;
  repAvatarUrl?: string;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const name = repName?.trim() || 'Someone from the team';
  const initial = name[0]?.toUpperCase() ?? '·';
  return (
    <div class="sp-msg sp-msg-call-invite">
      <div class="sp-call-invite-card">
        <div class="sp-call-invite-meta">
          {repAvatarUrl ? (
            <img class="sp-call-invite-avatar" src={repAvatarUrl} alt={name} />
          ) : (
            <span class="sp-call-invite-avatar sp-call-invite-avatar--fallback">
              {initial}
            </span>
          )}
          <div class="sp-call-invite-text">
            <div class="sp-call-invite-name">{name} wants to talk</div>
            <div class="sp-call-invite-sub">Quick audio call · faster than typing</div>
          </div>
        </div>
        <div class="sp-call-invite-actions">
          <button
            type="button"
            class="sp-call-invite-btn sp-call-invite-btn--decline"
            onClick={onDecline}
          >
            Not now
          </button>
          <button
            type="button"
            class="sp-call-invite-btn sp-call-invite-btn--accept"
            onClick={onAccept}
          >
            Join call
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Slice E — centered system-line announcement. Rendered when the rep
 * joins or releases the conversation so the visitor has context for
 * the suddenly-different author. Visually thin + muted so it reads as
 * meta, not as another participant.
 */
export function SystemLine({ text }: { text: string }) {
  return (
    <div class="sp-msg sp-msg-system">
      <div class="sp-msg-system-rule" />
      <div class="sp-msg-system-text">{text}</div>
      <div class="sp-msg-system-rule" />
    </div>
  );
}

/**
 * Slice E — "rep is typing…" indicator. Mirrors RepBubble's meta strip
 * (avatar + name) above the dots so the visitor sees WHO is composing,
 * not just a generic typing animation. Imperatively mounted + cleared
 * by the rep-typing handler in ui.tsx so we can swap it for the final
 * rep bubble when their message lands.
 */
export function RepTypingBubble({
  repName,
  repAvatarUrl,
}: {
  repName?: string;
  repAvatarUrl?: string;
}) {
  const name = repName?.trim() || 'Team';
  const initial = name[0]?.toUpperCase() ?? '·';
  return (
    <div class="sp-msg sp-msg-assistant sp-msg-rep sp-msg-rep-typing">
      <div class="sp-msg-rep-meta">
        {repAvatarUrl ? (
          <img
            class="sp-msg-rep-avatar"
            src={repAvatarUrl}
            alt={name}
          />
        ) : (
          <span class="sp-msg-rep-avatar sp-msg-rep-avatar--fallback">
            {initial}
          </span>
        )}
        <span class="sp-msg-rep-name">{name} is typing</span>
      </div>
      <div class="sp-msg-content">
        <div class="sp-typing">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  );
}
